<template>
  <fullscreen-overlay-frame :title="title"
                            icon="control_point_duplicate"
                            :color="color"
                            centered
                            closable
                            @close="close">
    <template v-slot:content>

      <!-- add gateway button -->
      <v-card :loading="loading.site"
              :disabled="gatewayAdded || !isSiteAdmin"
              class="mb-4"
              @click="addGateway">
        <v-card-title class="pb-0">
          <v-icon class="material-icons-outlined" color="primary" left>broadcast_on_personal</v-icon>
          <span class="primary--text font-weight-bold font-size-04"
                v-text="$t('add-home-energy-component-dialog.gateway.title')"/>
          <v-spacer/>
          <v-icon color="primary" class="material-icons-outlined">
            chevron_right
          </v-icon>
        </v-card-title>
        <v-card-text class="pl-12 font-size-02"
                     v-text="$t('add-home-energy-component-dialog.gateway.info')"/>
      </v-card>

      <!-- add enode button -->
      <v-card :disabled="!isSiteAdmin"
              v-if="hasEnode"
              class="mb-4"
              @click="addEnodeConnection">
        <v-card-title class="pb-0">
          <v-icon class="material-icons-outlined" color="primary" left>electric_car</v-icon>
          <span class="primary--text font-weight-bold font-size-04"
                v-text="$t('add-enode-connection.title')"/>
          <v-spacer/>
          <v-icon color="primary" class="material-icons-outlined">
            chevron_right
          </v-icon>
        </v-card-title>
        <v-card-text class="pl-12 font-size-02"
                     v-text="$t('add-enode-connection.info')"/>
      </v-card>

      <!-- add ocpp charging station button -->
      <v-card v-if="hasOcppChargingStations"
              :disabled="!isSiteAdmin"
              class="mb-4"
              @click="addOcppChargingStation">
        <v-card-title class="pb-0">
          <v-icon class="material-icons-outlined" color="primary" left>ev_station</v-icon>
          <span class="primary--text font-weight-bold font-size-04"
                v-text="$t('add-charging-station-dialog.ocpp.add')"/>
          <v-spacer/>
          <v-icon color="primary" class="material-icons-outlined">
            chevron_right
          </v-icon>
        </v-card-title>
        <v-card-text class="pl-12 font-size-02"
                     v-text="$t('add-charging-station-dialog.ocpp.info')"/>
      </v-card>

      <!-- add device button -->
      <v-card :disabled="!gatewayAdded || !isSiteAdmin"
              class="mb-4"
              @click="addDevice(false)">
        <v-card-title class="pb-0">
          <v-icon class="material-icons-outlined" color="primary" left>widgets</v-icon>
          <span class="primary--text font-weight-bold font-size-04"
                v-text="$t('add-home-energy-component-dialog.device.title')"/>
          <v-spacer/>
          <v-icon color="primary" class="material-icons-outlined">
            chevron_right
          </v-icon>
        </v-card-title>
        <v-card-text class="pl-12 font-size-02"
                     v-text="$t('add-home-energy-component-dialog.device.info')"/>
      </v-card>

    </template>

  </fullscreen-overlay-frame>
</template>

<script>
import FullscreenOverlayFrame from "@/templates/dialogs/FullscreenOverlayFrame";
import inAppBrowser from "@/scripts/inAppBrowser";
import config from "@/config/config.app.json";
import {createEnodeLib} from "@/plugins/enodePlugin";

export default {
  name: 'AddHomeEnergyComponentDialog',
  components: {
    FullscreenOverlayFrame
  },

  props: ['data'],

  data: function () {
    return {
      title: this.$t('add-home-energy-component-dialog.title'),
      color: 'primary',
      enodePlugin: createEnodeLib(),
      loading: {
        site: true
      },
      gatewayAdded: false,
    }
  },

  computed: {
    isSiteAdmin() {
      return this.$rhAuth.isSiteAdmin()
    },

    hasEnode() {
      return Object.hasOwn(config, 'enodeRedirect') // non-boolean value
    },

    hasOcppChargingStations() {
      return (/true/).test(config.hasOcppChargingStations)
    }
  },

  methods: {
    getSiteInfo() {
      this.$rhRequest.sendGet({
        endpoint: "site/get-general-information",
      }, (resp) => {
        this.loading.site = false
        this.siteType = resp?.data?.data?.siteType
        this.gatewayAdded =  this.siteType !== 'no_gateway'
      }, (err) => {
        console.error(err)
        this.loading.site = false
      })
    },

    /**
     * open gateway dialog
     */
    addGateway() {
      this.close()
      this.$root.bisadialog.toggle('addGateway')
      this.$root.bisadialog.callDialogInit('addGateway')
    },

    /**
     * open add enode dialog
     */
    addEnodeConnection() {
      this.close()
      this.enodePlugin.addEnodeConnection(config.enodeRedirect).then((linkUrl) => {
        if (linkUrl) {
          let event = {
            event: 'loadstart',
            callback: (params) => {
              let [hash, query] = params.url.split('#')[1].split('?')

              if (hash === "/enodeRedirect") {
                if (query) {
                  let params = Object.fromEntries(new URLSearchParams(query))

                  this.$root.bisatoast.error({
                    message: this.$t('app.generic-error'),
                    showCloseBtn: true
                  })

                  console.error(hash, params?.error)
                  console.error(hash, params?.error_description)
                } else {
                  this.enodePlugin.getData()
                  this.$root.bisatoast.success({
                    message: this.$t("enode.toast.success"),
                    showCloseBtn: true
                  })
                }

                window.enodeBrowser.close()
              }
            }
          }
          inAppBrowser.openUrl(linkUrl, '_blank', event)
        }
        this.loading = false
      }).catch(() => {
        this.loading = false
      })
    },

    /**
     * open ocpp dialog
     */
    addOcppChargingStation() {
      this.close()
      this.$root.bisadialog.toggle('addOcppChargePoint')
    },

    /**
     * open add device dialog
     */
    addDevice(secure) {
      this.close()
      this.$root.bisadialog.toggle('addDevice', true, {secure: secure})
      this.$root.bisadialog.callDialogInit('addDevice')
    },

    /**
     * close dialog
     */
    close() {
      this.$root.bisadialog.toggle('addHomeEnergyComponent')
    }
  },

  mounted() {
    this.getSiteInfo()
  }
};
</script>

